import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ShoppingBag, Check, MessageCircle } from "lucide-react";
import { api, formatPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Seo } from "../components/Seo";
import { ShareButtons } from "../components/ShareButtons";
import { ChatModal } from "../components/ChatModal";

export default function ArtworkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, items } = useCart();
  const { user } = useAuth();
  const [art, setArt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/artworks/${id}`)
      .then((res) => setArt(res.data))
      .catch(() => setArt(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="pt-40 text-center text-zinc-500">Chargement…</div>;
  if (!art)
    return (
      <div className="pt-40 text-center text-zinc-400 min-h-screen">
        Œuvre introuvable. <Link to="/galerie" className="text-[#60a5fa] underline">Retour à la galerie</Link>
      </div>
    );

  const inCart = items.find((i) => i.id === art.id);

  return (
    <div className="pt-28 pb-24 max-w-7xl mx-auto px-6 lg:px-10 min-h-screen" data-testid="artwork-detail-page">
      <Seo
        title={`${art.title} — ${art.artist_name}`}
        description={art.description || `${art.title}, œuvre de ${art.artist_name}. ${art.technique} ${art.dimensions}. ${formatPrice(art.price)}.`}
        image={art.image_url}
        path={`/oeuvre/${art.id}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "VisualArtwork",
          name: art.title,
          image: art.image_url,
          description: art.description,
          artform: art.category,
          artMedium: art.technique,
          creator: { "@type": "Person", name: art.artist_name },
          offers: {
            "@type": "Offer",
            price: art.price,
            priceCurrency: "EUR",
            availability: art.sold ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
          },
        }}
      />
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-10 transition-colors"
        data-testid="back-button"
      >
        <ArrowLeft className="h-4 w-4" /> Retour
      </button>

      <div className="grid lg:grid-cols-2 gap-14">
        <div className="bg-[#0d0d0d] overflow-hidden">
          <img src={art.image_url} alt={art.title} className="w-full object-contain max-h-[80vh]" data-testid="artwork-image" />
        </div>

        <div className="lg:sticky lg:top-28 self-start">
          <p className="text-xs uppercase tracking-[0.3em] text-[#60a5fa] mb-4">{art.category}</p>
          <h1 className="font-display text-5xl text-white tracking-tight leading-tight" data-testid="artwork-title">{art.title}</h1>
          <p className="text-zinc-400 mt-3 text-lg">par {art.artist_name}</p>

          <p className="text-3xl text-white mt-8 font-light" data-testid="artwork-price">{formatPrice(art.price)}</p>

          <div className="mt-8 space-y-4 text-sm">
            {art.technique && <DetailRow label="Technique" value={art.technique} />}
            {art.dimensions && <DetailRow label="Dimensions" value={art.dimensions} />}
            {art.year && <DetailRow label="Année" value={art.year} />}
          </div>

          {art.description && (
            <p className="text-zinc-300 leading-relaxed mt-8">{art.description}</p>
          )}

          {art.sold ? (
            <div className="mt-10 inline-block bg-[#E11D48] text-white px-8 py-4 text-sm uppercase tracking-widest" data-testid="sold-badge">
              Œuvre vendue
            </div>
          ) : (
            <button
              data-testid="detail-add-to-cart"
              onClick={() => addItem(art)}
              className="mt-10 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-10 py-4 text-sm tracking-wide transition-colors"
            >
              {inCart ? <><Check className="h-4 w-4" /> Dans le panier</> : <><ShoppingBag className="h-4 w-4" /> Ajouter au panier</>}
            </button>
          )}

          <ShareButtons title={art.title} />

          {art.artist_id && (!user || user.id !== art.artist_id) && (
            <button
              data-testid="contact-artist-button"
              onClick={() => setChatOpen(true)}
              className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-3 border border-white/20 hover:border-white text-white px-8 py-3.5 text-sm tracking-wide transition-colors"
            >
              <MessageCircle className="h-4 w-4" /> Contacter l'artiste
            </button>
          )}

          {art.artist_bio && (
            <div className="mt-12 pt-8 border-t border-white/10">
              <h3 className="font-display text-2xl text-white mb-3">À propos de l'artiste</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{art.artist_bio}</p>
            </div>
          )}
        </div>
      </div>

      {chatOpen && (
        <ChatModal
          receiverId={art.artist_id}
          receiverName={art.artist_name}
          contextType="gallery"
          contextId={art.id}
          contextTitle={art.title}
          onClose={() => setChatOpen(false)}
        />
      )}
    </div>
  );
}

const DetailRow = ({ label, value }) => (
  <div className="flex justify-between border-b border-white/5 pb-3">
    <span className="text-zinc-500 uppercase tracking-wider text-xs">{label}</span>
    <span className="text-zinc-200">{value}</span>
  </div>
);
